import React, { useMemo, useState, useRef, useEffect } from 'react';
import StatePill from '../StatePill';
import {
  locName, isArchived, siteStatusLabel, devicesAtSite,
  consumableTotal, clientLetter, subLocationsOf,
} from '../../utils/locations';
import { siteDeviceHealth, ATTENTION_STATES } from '../../utils/siteDeviceHealth';
import Pager, { usePaged } from './Pager';

// The Locations index, per the Locations redesign.
//
// Two tabs share the header and tile strip:
//   Sites                 customer sites devices are deployed to
//   Employee assignments  devices signed out to a named person
//
// The tabs answer different questions about the same fleet — "where is it" and
// "who has it" — which is why they sit together rather than on separate screens.
// The Employee tab lives in LocationsPeople.js; this file owns the Sites tab and
// the shared chrome.

const TILE_GRID = { display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12 };

// Index table columns. Kept in one place because the header and every row must
// use the identical track list or the columns drift apart.
const COLS = 'minmax(0,1.7fr) 110px 120px 130px minmax(0,1.2fr) 36px';

function Tile({ label, value, sub, tone }) {
  // Only the warning tile changes colour; the rest stay neutral so a single
  // amber tile reads as "this one needs you" rather than as decoration.
  const warn = tone === 'warning';
  return (
    <div className="card" style={{ padding: '14px 16px', ...(warn ? { borderColor: 'var(--warning-border)' } : {}) }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</div>
      <div className="tnum" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1, marginTop: 8, color: warn ? 'var(--warning-text)' : 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 12, marginTop: 6, color: warn ? 'var(--warning-text)' : 'var(--text-muted)' }}>{sub}</div>
    </div>
  );
}

// Segmented control used for both the status filter and the people sub-filter.
function Segmented({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border-default)', borderRadius: 6, overflow: 'hidden' }}>
      {options.map(([key, label], i) => {
        const on = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            style={{
              display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 12px',
              fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
              border: 'none', borderLeft: i === 0 ? 'none' : '1px solid var(--border-default)',
              background: on ? 'var(--segmented-on-bg)' : 'var(--surface-default)',
              color: on ? 'var(--segmented-on-fg)' : 'var(--text-muted)',
              fontWeight: on ? 600 : 400,
            }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

// Client filter. A dropdown rather than a chip row because the client list grows
// with the customer base and a row of chips would wrap unpredictably.
function ClientSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const label = value === 'All' ? 'All clients' : value;

  return (
    <div ref={ref} style={{ position: 'relative', width: 200, flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 32, padding: '0 10px',
          border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--surface-default)',
          cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)',
        }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>{label}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--text-muted)' }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div role="listbox" style={{ position: 'absolute', left: 0, right: 0, top: 36, zIndex: 30, background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 6, boxShadow: 'var(--shadow-lg)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 34, padding: '0 10px',
                cursor: 'pointer', border: 'none', borderBottom: '1px solid var(--border-subtle)',
                fontFamily: 'inherit', textAlign: 'left',
                background: o.value === value ? 'var(--surface-inset)' : 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = o.value === value ? 'var(--surface-inset)' : 'transparent'; }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text-primary)', fontWeight: o.value === value ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{o.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// The Add location / Direct deploy buttons and the tab strip live in App.js
// alongside the page header, so this component owns only the tiles and table.
function LocationsIndex({ st, getStats, lightbugLive, onOpenSite, sites }) {
  const [query, setQuery] = useState('');
  const [client, setClient] = useState('All');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(1);

  // Per-site roll-ups. Computed once for every site because the tiles, the row
  // sub-text and the client counts all need the same numbers.
  const rows = useMemo(() => sites.map(loc => {
    const devs = devicesAtSite(st.devices || [], loc);
    const attention = devs.filter(d => {
      const live = d.deviceId ? (lightbugLive || {})[String(d.deviceId)] : null;
      return ATTENTION_STATES.includes(siteDeviceHealth(d, getStats(d), live));
    }).length;
    return {
      loc,
      name: locName(loc),
      client: loc.customer || '',
      region: loc.address || '',
      siteId: loc.siteId || '',
      archived: isArchived(loc),
      devices: devs.length,
      attention,
      consumables: consumableTotal(loc, st.consumableStock || [], st.consumableQty || {}, st.consumables || []),
      contact: loc.contact || '',
      phone: loc.phone || '',
      // Portal projects sitting within this site. Counted here rather than in the row so
      // the tile strip can report how much of the estate is organised into projects.
      subs: subLocationsOf(loc, st.portalProjects, st.portalSnapshot, st.devices || []),
      // Sites the OWL API created, rather than ones a person set up. Marked at creation
      // and back-filled once on load for sites that predate the field.
      fromPortal: loc.source === 'portal',
    };
  }), [sites, st.devices, st.consumableStock, st.consumableQty, st.consumables, st.portalProjects, st.portalSnapshot, getStats, lightbugLive]);

  const clientOptions = useMemo(() => {
    const names = Array.from(new Set(rows.map(r => r.client).filter(Boolean))).sort();
    return [{ value: 'All', label: 'All clients', count: rows.length }]
      .concat(names.map(n => ({ value: n, label: n, count: rows.filter(r => r.client === n).length })));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (client !== 'All' && r.client !== client) return false;
      if (status === 'Active' && r.archived) return false;
      if (status === 'Archived' && !r.archived) return false;
      if (!q) return true;
      return `${r.name} ${r.client} ${r.region} ${r.siteId}`.toLowerCase().includes(q);
    });
  }, [rows, query, client, status]);

  // Any change to the search or filters puts you back on page 1 — staying on
  // page 3 of a freshly narrowed list would show an empty page.
  useEffect(() => { setPage(1); }, [query, client, status]);

  const { paged, page: safePage, pageCount, from, to } = usePaged(filtered, page, setPage);

  const activeCount = rows.filter(r => !r.archived).length;
  const deployed = rows.reduce((n, r) => n + r.devices, 0);
  // Sub-locations across the estate, and how many deployed devices actually sit on one.
  // The gap between `onProject` and `deployed` is the kit the portal cannot account for.
  const subTotal = rows.reduce((n, r) => n + r.subs.length, 0);
  const onProject = rows.reduce((n, r) => n + r.subs.reduce((m, s) => m + s.deviceCount, 0), 0);
  const portalCount = rows.filter(r => r.fromPortal).length;
  const held = rows.reduce((n, r) => n + r.consumables, 0);
  // Only active sites count as "empty" — an archived site having nothing on it
  // is the expected outcome, not a prompt to go and deploy something.
  const empty = rows.filter(r => !r.archived && r.devices === 0).length;

  return (
    <>
      <div style={TILE_GRID}>
        <Tile label="Sites" value={rows.length} sub={`${activeCount} active · ${rows.length - activeCount} archived`} />
        <Tile
          label="Devices on site"
          value={deployed.toLocaleString('en-GB')}
          sub={subTotal
            ? `${onProject.toLocaleString('en-GB')} on a project · ${subTotal} sub-location${subTotal === 1 ? '' : 's'}`
            : 'Across all active sites'} />
        <Tile label="Consumables held" value={held.toLocaleString('en-GB')} sub="Items away from the office" />
        <Tile
          label="Sites with nothing on them"
          value={empty}
          tone={empty ? 'warning' : undefined}
          sub={empty ? 'Candidates for a deployment' : 'Every site has devices'} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 16, display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <div style={{ height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--surface-default)', width: 280, maxWidth: '100%' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--text-muted)' }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search site, client, region or site ID"
              aria-label="Search sites"
              style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)' }} />
          </div>
          <ClientSelect value={client} options={clientOptions} onChange={setClient} />
          <div style={{ flex: 1 }} />
          <Segmented
            options={[['All', 'All'], ['Active', 'Active'], ['Archived', 'Archived']]}
            value={status}
            onChange={setStatus} />
        </div>

        {/* Header */}
        <div style={{ flexShrink: 0, display: 'grid', gridTemplateColumns: COLS, gap: '0 16px', height: 36, alignItems: 'center', padding: '0 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          <div>Site</div>
          <div>Status</div>
          <div style={{ textAlign: 'right' }}>Devices</div>
          <div style={{ textAlign: 'right' }}>Consumables</div>
          <div>Site contact</div>
          <div />
        </div>

        {/* Rows */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {paged.map(r => (
            <div
              key={r.loc.id || r.name}
              role="button"
              tabIndex={0}
              onClick={() => onOpenSite(r.loc)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenSite(r.loc); } }}
              style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 16px', alignItems: 'center', minHeight: 56, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', background: r.archived ? 'var(--surface-inset)' : 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = r.archived ? 'var(--surface-inset)' : 'transparent'; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div
                  title={r.fromPortal ? 'Created automatically from the OWL portal' : undefined}
                  style={{ position: 'relative', flexShrink: 0, width: 26, height: 26, borderRadius: 6, background: r.archived ? 'var(--text-subtle)' : (r.fromPortal ? 'var(--portal-default)' : 'var(--brand-default)'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff' }}>
                  {clientLetter(r.client)}
                  {/* The colour alone is not an accessible signal, so the meaning is also
                      available to a screen reader and on hover. */}
                  <span className="sr-only">{r.fromPortal ? ' (created from the OWL portal)' : ''}</span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.client && <span style={{ fontWeight: 600, color: 'var(--brand-light)' }}>{r.client} </span>}
                    <span style={{ fontWeight: 500 }}>{r.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {r.region || 'No region'} · <span className="tnum">{r.siteId || '—'}</span>
                    {r.subs.length > 0 && (
                      <> · <span className="tnum">{r.subs.length}</span> sub-location{r.subs.length === 1 ? '' : 's'}</>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <StatePill state={siteStatusLabel(r.loc)} tone={r.archived ? 'neutral' : 'success'} />
              </div>

              <div style={{ textAlign: 'right' }}>
                <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: r.devices ? 'var(--text-primary)' : 'var(--text-muted)' }}>{r.devices}</div>
                <div style={{ fontSize: 12, marginTop: 2, color: r.attention ? 'var(--warning-text)' : 'var(--text-muted)' }}>
                  {r.devices ? (r.attention ? `${r.attention} need attention` : 'all reporting') : 'none out'}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: r.consumables ? 'var(--text-primary)' : 'var(--text-muted)' }}>{r.consumables}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.consumables ? 'items held' : 'none held'}</div>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, color: r.contact ? 'var(--text-primary)' : 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.contact || 'Not set'}
                </div>
                <div className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{r.phone || '—'}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-subtle)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No locations match</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
                {query.trim()
                  ? `Nothing matches “${query.trim()}”. Clear the search or widen the filters.`
                  : 'Widen the client or status filter.'}
              </div>
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 36, padding: '4px 16px', background: 'var(--surface-inset)', borderTop: '1px solid var(--border-default)' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {filtered.length === 0
              ? 'No sites'
              : `${from}–${to} of ${filtered.length}${filtered.length === rows.length ? '' : ` (filtered from ${rows.length})`} site${filtered.length === 1 ? '' : 's'}`}
          </span>
          {/* The avatar colour is only meaningful if it is explained somewhere. Shown
              only when there is actually a portal-created site to explain. */}
          {portalCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--portal-default)' }} />
              {portalCount} created from the OWL portal
            </span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <Pager page={safePage} pageCount={pageCount} setPage={setPage} label="Sites" />
          </div>
        </div>
      </div>
    </>
  );
}

export default LocationsIndex;
export { Tile, Segmented, TILE_GRID };
